/**
 * Moderation Detail Modal
 * Comprehensive review interface for moderation queue items
 * Solis Games - Phase 2.1 Implementation
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { moderationService } from '../../../utils/moderationService';

export default function ModerationDetailModal({ item, onClose, onActionComplete }) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('content');

  // State for data
  const [violationHistory, setViolationHistory] = useState([]);
  const [actionHistory, setActionHistory] = useState([]);
  const [chatContext, setChatContext] = useState([]);
  const [moderatorNotes, setModeratorNotes] = useState('');

  // Loading states
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [loadingActions, setLoadingActions] = useState(true);
  const [loadingContext, setLoadingContext] = useState(false);

  useEffect(() => {
    loadData();
  }, [item.id]);

  const loadData = async () => {
    // Load violation history
    setLoadingHistory(true);
    const { data: violations } = await moderationService.getUserViolationHistory(item.reported_user_id, 10);
    setViolationHistory(violations || []);
    setLoadingHistory(false);

    // Load action history
    setLoadingActions(true);
    const { data: actions } = await moderationService.getActionHistory(item.id);
    setActionHistory(actions || []);
    setLoadingActions(false);

    // Load chat context if it's a chat message
    if (item.content_type === 'chat_message') {
      setLoadingContext(true);
      const { data: context } = await moderationService.getChatContext(
        item.content_id,
        item.context_data?.channel_id,
        20
      );
      setChatContext(context || []);
      setLoadingContext(false);
    }
  };

  const handleAction = async (action, status) => {
    if (!moderatorNotes.trim() && action !== 'approved') {
      alert('Please add notes explaining your decision');
      return;
    }

    setLoading(true);

    try {
      // Perform the moderation action
      const { error: actionError } = await moderationService.performAction(
        item.id,
        user.id,
        action,
        status,
        moderatorNotes
      );

      if (actionError) throw new Error(actionError);

      // Execute additional actions based on type
      if (action === 'content_deleted') {
        const { error: deleteError } = await moderationService.deleteContent(item);
        if (deleteError) throw new Error(deleteError);
      } else if (action === 'user_warned') {
        const { error: warnError } = await moderationService.warnUser(item, moderatorNotes);
        if (warnError) throw new Error(warnError);
      } else if (action === 'user_banned') {
        const { error: banError } = await moderationService.banUser(item, user.id, moderatorNotes);
        if (banError) throw new Error(banError);
      }

      // Success - close modal and refresh
      onActionComplete();
      onClose();
    } catch (error) {
      console.error('Error performing moderation action:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-red-500/10 text-red-500 border-red-500/20';
      case 'high': return 'bg-orange-500/10 text-orange-500 border-orange-500/20';
      case 'medium': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'low': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      default: return 'bg-gray-500/10 text-gray-500 border-gray-500/20';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500/10 text-yellow-500';
      case 'in_review': return 'bg-blue-500/10 text-blue-500';
      case 'approved': return 'bg-green-500/10 text-green-500';
      case 'rejected': return 'bg-red-500/10 text-red-500';
      case 'escalated': return 'bg-purple-500/10 text-purple-500';
      default: return 'bg-gray-500/10 text-gray-500';
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-lg w-full max-w-6xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-4">
            <Icon name="Shield" size={24} className="text-primary" />
            <div>
              <h2 className="text-xl font-bold text-foreground">Review Content</h2>
              <p className="text-sm text-muted-foreground">
                {item.content_type.replace('_', ' ')} • Flagged {formatDate(item.flagged_at)}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" iconName="X" onClick={onClose} disabled={loading} />
        </div>

        {/* Status Badges */}
        <div className="px-6 py-4 border-b border-border bg-muted/30">
          <div className="flex flex-wrap items-center gap-3">
            <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium border ${getSeverityColor(item.severity)}`}>
              <Icon name="AlertTriangle" size={14} className="mr-1.5" />
              {item.severity} severity
            </span>
            <span className={`inline-flex items-center px-3 py-1 rounded text-sm font-medium ${getStatusColor(item.status)}`}>
              {item.status}
            </span>
            {item.auto_flagged && (
              <span className="inline-flex items-center px-3 py-1 rounded text-sm font-medium bg-purple-500/10 text-purple-500">
                <Icon name="Zap" size={14} className="mr-1.5" />
                Auto-flagged ({(item.confidence_score * 100).toFixed(0)}%)
              </span>
            )}
            <span className="text-sm text-muted-foreground">
              {item.user_violation_count} recent violations
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
          {/* Main Content Area (Left 2/3) */}
          <div className="lg:col-span-2 space-y-6">
            {/* Tabs */}
            <div className="flex border-b border-border">
              <button
                onClick={() => setActiveTab('content')}
                className={`px-4 py-2 font-medium text-sm transition-colors ${
                  activeTab === 'content'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Content
              </button>
              {item.content_type === 'chat_message' && (
                <button
                  onClick={() => setActiveTab('context')}
                  className={`px-4 py-2 font-medium text-sm transition-colors ${
                    activeTab === 'context'
                      ? 'text-primary border-b-2 border-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  Conversation Context
                </button>
              )}
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 py-2 font-medium text-sm transition-colors ${
                  activeTab === 'history'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Action History
              </button>
            </div>

            {/* Tab Content */}
            <div className="min-h-[300px]">
              {/* Content Tab */}
              {activeTab === 'content' && (
                <div className="space-y-4">
                  <div className="bg-muted/50 rounded-lg p-4 border border-border">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="font-medium text-foreground">Flagged Content</h3>
                      <span className="text-xs text-muted-foreground">{item.game_title || 'N/A'}</span>
                    </div>
                    <p className="text-foreground whitespace-pre-wrap break-words">
                      {item.content_snapshot?.message || item.flag_reason}
                    </p>
                    {item.content_snapshot?.toxicity_score && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <span className="text-xs text-muted-foreground">
                          Toxicity Score: {(item.content_snapshot.toxicity_score * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="bg-card border border-border rounded-lg p-4">
                    <h3 className="font-medium text-foreground mb-3">Flag Details</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Reason:</span>
                        <span className="text-foreground font-medium">{item.flag_reason}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">User:</span>
                        <span className="text-foreground">{item.reported_user_name} ({item.reported_user_email})</span>
                      </div>
                      {item.reporter_user_id && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Reported by:</span>
                          <span className="text-foreground">User report</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Flagged at:</span>
                        <span className="text-foreground">{formatDate(item.flagged_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Context Tab (Chat Messages) */}
              {activeTab === 'context' && item.content_type === 'chat_message' && (
                <div className="space-y-2">
                  {loadingContext ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : chatContext.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No conversation context available</p>
                  ) : (
                    chatContext.map((msg) => (
                      <div
                        key={msg.id}
                        className={`p-3 rounded-lg border ${
                          msg.id === item.content_id
                            ? 'bg-red-500/10 border-red-500/30'
                            : 'bg-muted/30 border-border'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <span className="text-sm font-medium text-foreground">
                            {msg.user?.full_name || 'Unknown User'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(msg.created_at).toLocaleTimeString()}
                          </span>
                        </div>
                        <p className="text-sm text-foreground">{msg.message}</p>
                        {msg.id === item.content_id && (
                          <div className="mt-2 pt-2 border-t border-red-500/20">
                            <span className="text-xs text-red-500 font-medium flex items-center">
                              <Icon name="AlertTriangle" size={12} className="mr-1" />
                              Flagged Message
                            </span>
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* History Tab */}
              {activeTab === 'history' && (
                <div className="space-y-3">
                  {loadingActions ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : actionHistory.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8">No actions taken yet</p>
                  ) : (
                    actionHistory.map((action) => (
                      <div key={action.id} className="bg-muted/30 border border-border rounded-lg p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Icon name="User" size={16} className="text-muted-foreground" />
                            <span className="text-sm font-medium text-foreground">
                              {action.moderator?.full_name}
                            </span>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {formatDate(action.created_at)}
                          </span>
                        </div>
                        <div className="space-y-1">
                          <p className="text-sm text-foreground">
                            <span className="font-medium">{action.action_type}</span>
                            {action.previous_status && action.new_status && (
                              <span className="text-muted-foreground">
                                {' '}• {action.previous_status} → {action.new_status}
                              </span>
                            )}
                          </p>
                          {action.notes && (
                            <p className="text-sm text-muted-foreground italic">&quot;{action.notes}&quot;</p>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Moderator Notes */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-foreground">
                Moderator Notes <span className="text-red-500">*</span>
              </label>
              <textarea
                value={moderatorNotes}
                onChange={(e) => setModeratorNotes(e.target.value)}
                placeholder="Explain your decision and any relevant context..."
                className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary min-h-[100px] resize-y"
                disabled={loading}
              />
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
              <Button
                variant="success"
                onClick={() => handleAction('approved', 'approved')}
                disabled={loading}
                iconName="CheckCircle"
              >
                Approve
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAction('content_deleted', 'rejected')}
                disabled={loading}
                iconName="Trash2"
              >
                Delete Content
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAction('user_warned', 'rejected')}
                disabled={loading}
                iconName="AlertTriangle"
              >
                Warn User
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleAction('user_banned', 'rejected')}
                disabled={loading}
                iconName="Ban"
              >
                Ban User
              </Button>
              <Button
                variant="outline"
                onClick={() => handleAction('escalated', 'escalated')}
                disabled={loading}
                iconName="ArrowUp"
              >
                Escalate
              </Button>
            </div>
          </div>

          {/* Sidebar (Right 1/3) */}
          <div className="space-y-6">
            {/* User Violation History */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-4 flex items-center">
                <Icon name="History" size={18} className="mr-2" />
                User Violation History
              </h3>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-4">
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : violationHistory.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">No previous violations</p>
              ) : (
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {violationHistory.map((violation) => (
                    <div key={violation.id} className="bg-muted/30 rounded p-3 text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${getSeverityColor(violation.severity)}`}>
                          {violation.severity}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(violation.flagged_at).toLocaleDateString()}
                        </span>
                      </div>
                      <p className="text-foreground line-clamp-2 mb-1">{violation.flag_reason}</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(violation.status)}`}>
                        {violation.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Stats */}
            <div className="bg-card border border-border rounded-lg p-4">
              <h3 className="font-medium text-foreground mb-4">User Statistics</h3>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Total Reports</span>
                  <span className="text-lg font-bold text-foreground">{item.user_total_reports || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">Recent Violations (30d)</span>
                  <span className="text-lg font-bold text-red-500">{item.user_violation_count || 0}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
